import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faChevronDown,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, CSSProperties } from 'react';

import './SearchBar.css';

type ColumnInput =
  | string
  | { label: string; value: string }
  | [label: string, value: string];

type ColumnsProp =
  | readonly ColumnInput[]
  | Record<string, string | undefined>
  | undefined;

interface ColumnOption {
  label: string;
  value: string;
}

const joinClassNames = (...values: Array<string | undefined>) =>
  values.filter(Boolean).join(' ');

const normalizeColumns = (columns: ColumnsProp): ColumnOption[] => {
  if (!columns) {
    return [];
  }

  if (Array.isArray(columns)) {
    return columns.map((entry) => {
      if (typeof entry === 'string') {
        return { label: entry, value: entry } satisfies ColumnOption;
      }

      if (Array.isArray(entry)) {
        const [label, value] = entry;
        return { label, value: value ?? label } satisfies ColumnOption;
      }

      const { label, value } = entry;
      return {
        label,
        value: value ?? label,
      } satisfies ColumnOption;
    });
  }

  const recordEntries = Object.entries(
    columns as Record<string, string | undefined>
  );

  return recordEntries.map(([label, value]) => ({
    label,
    value: label,
  }));
};

export interface SearchBarProps {
  onSearch: (term: string, column?: string | null) => void;
  columns?: ColumnsProp;
  defaultSelectedColumn?: string;
  placeholder?: string;
  filterPlaceholder?: string;
  debounceMs?: number;
  value?: string;
  defaultValue?: string;
  onInputChange?: (value: string) => void;
  onColumnChange?: (column: string | null) => void;
  allowClear?: boolean;
  className?: string;
  inputClassName?: string;
  filterButtonClassName?: string;
  disabled?: boolean;
  style?: CSSProperties;
  autoFocus?: boolean;
  name?: string;
  'aria-label'?: string;
}

/**
 * Shared SearchBar component with optional column filter support.
 */
const SearchBar = ({
  onSearch,
  columns,
  defaultSelectedColumn,
  placeholder = 'Search',
  filterPlaceholder = 'Filter',
  debounceMs = 0,
  value,
  defaultValue,
  onInputChange,
  onColumnChange,
  allowClear = true,
  className,
  inputClassName,
  filterButtonClassName,
  disabled = false,
  style,
  autoFocus,
  name,
  'aria-label': ariaLabel,
}: SearchBarProps) => {
  const columnOptions = useMemo(() => normalizeColumns(columns), [columns]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState<string>(
    () => defaultValue ?? ''
  );
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const pendingColumnChange = useRef(false);
  const isFirstRender = useRef(true);
  const isControlled = value !== undefined;
  const searchTerm = isControlled ? value : uncontrolledValue;

  const findColumn = useCallback(
    (column?: string | null) => {
      if (!column) {
        return null;
      }

      return (
        columnOptions.find(
          (option) => option.value === column || option.label === column
        ) ?? null
      );
    },
    [columnOptions]
  );

  const [selectedColumn, setSelectedColumn] = useState<ColumnOption | null>(
    () => findColumn(defaultSelectedColumn)
  );
  const selectedColumnValue = selectedColumn?.value ?? null;

  useEffect(() => {
    if (!columnOptions.length) {
      setSelectedColumn(null);
      return;
    }

    if (
      selectedColumn &&
      columnOptions.some((option) => option.value === selectedColumn.value)
    ) {
      return;
    }

    if (defaultSelectedColumn) {
      const match = findColumn(defaultSelectedColumn);
      setSelectedColumn(match);
      return;
    }

    setSelectedColumn(null);
  }, [columnOptions, defaultSelectedColumn, findColumn, selectedColumn]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const latestOnSearch = useRef(onSearch);

  useEffect(() => {
    latestOnSearch.current = onSearch;
  }, [onSearch]);

  const emitSearch = useCallback((term: string, column: string | null) => {
    latestOnSearch.current(term, column);
  }, []);

  useEffect(() => {
    if (pendingColumnChange.current) {
      pendingColumnChange.current = false;
      return;
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (searchTerm === '' && selectedColumnValue === null) {
        return;
      }
    }

    if (debounceMs <= 0) {
      emitSearch(searchTerm, selectedColumnValue);
      return;
    }

    const timer = window.setTimeout(() => {
      emitSearch(searchTerm, selectedColumnValue);
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [debounceMs, emitSearch, searchTerm, selectedColumnValue]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (!isControlled) {
      setUncontrolledValue(nextValue);
    }

    onInputChange?.(nextValue);
  };

  const handleOptionSelect = (option: ColumnOption | null) => {
    pendingColumnChange.current = true;
    setSelectedColumn(option);
    setIsDropdownOpen(false);
    const nextValue = option?.value ?? null;
    onColumnChange?.(nextValue);
    emitSearch(searchTerm, nextValue);
  };

  const hasColumns = columnOptions.length > 0;
  const rootClassName = joinClassNames('search-bar', className);
  const inputWrapperClass = joinClassNames(
    'search-bar__input-wrapper',
    hasColumns ? 'search-bar__input-wrapper--with-filter' : undefined
  );
  const filterButtonClass = joinClassNames(
    'search-bar__filter-button',
    filterButtonClassName
  );

  return (
    <div className={rootClassName} style={style}>
      <div className={inputWrapperClass}>
        <FontAwesomeIcon icon={faSearch} className="search-bar__icon" />
        <input
          type="text"
          className={joinClassNames('search-bar__input', inputClassName)}
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          disabled={disabled}
          autoFocus={autoFocus}
          name={name}
          aria-label={ariaLabel ?? placeholder}
        />
      </div>
      {hasColumns && (
        <div className="search-bar__filter">
          <button
            type="button"
            className={filterButtonClass}
            onClick={() => setIsDropdownOpen((open) => !open)}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            disabled={disabled}
            ref={triggerRef}
          >
            <FontAwesomeIcon
              icon={faFilter}
              className="search-bar__filter-icon"
            />
            <span className="search-bar__filter-label">
              {selectedColumn?.label ?? filterPlaceholder}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="search-bar__chevron"
            />
          </button>
          {isDropdownOpen && (
            <div
              className="search-bar__filter-menu"
              ref={menuRef}
              role="listbox"
            >
              {allowClear && (
                <button
                  type="button"
                  className="search-bar__filter-option search-bar__filter-option--placeholder"
                  onClick={() => handleOptionSelect(null)}
                >
                  {filterPlaceholder}
                </button>
              )}
              {columnOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={joinClassNames(
                    'search-bar__filter-option',
                    option.value === selectedColumn?.value
                      ? 'search-bar__filter-option--active'
                      : undefined
                  )}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
